import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useSite } from "../../lib/site";
import { mdToHtml } from "../../lib/text";
import { lazyImgs } from "../../lib/html";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useRecaptcha } from "../../hooks/useRecaptcha";

const inputCls = "w-full rounded-xl border border-site-border bg-transparent px-4 py-3 text-s-text placeholder:text-s-muted focus:outline-none focus:ring-2 focus:ring-[color:var(--s-primary)] transition-shadow";

export function ContactForm({ type = "contact" }) {
  const { site, t, ui } = useSite();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", district: "", area: "", kvkk: false, website: "" });
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const [showKvkk, setShowKvkk] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  // Phone: strip anything that isn't a digit, space, dash, parenthesis or leading +
  const setPhone = (e) => {
    const cleaned = e.target.value.replace(/[^\d+\s()\-]/g, "").replace(/(?!^)\+/g, "");
    setF({ ...f, phone: cleaned });
  };
  const p = `${type}-form`;
  const { getToken, enabled: captchaEnabled } = useRecaptcha(type === "volunteer" ? "volunteer_form" : "contact_form");
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: "" });
    try {
      const token = await getToken();
      await api.post("/public/forms", { ...f, type, recaptcha_token: token });
      setState({ busy: false, done: true, error: "" });
    } catch (err) {
      setState({ busy: false, done: false, error: errMsg(err) });
    }
  };

  if (state.done)
    return (
      <div data-testid={`${p}-success`} className="flex flex-col items-center justify-center text-center gap-4 py-12">
        <CheckCircle2 size={48} className="text-s-primary" />
        <p className="text-xl font-semibold">{ui.sent}</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={p}>
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" hidden aria-hidden />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required minLength={2} placeholder={ui.name} value={f.name} onChange={set("name")} className={inputCls} data-testid={`${p}-name`} />
        <input required type="email" placeholder={ui.email} value={f.email} onChange={set("email")} className={inputCls} data-testid={`${p}-email`} />
      </div>
      <input type="tel" inputMode="tel" autoComplete="tel" pattern="[0-9+\s\-()]{6,}" placeholder={ui.phone} value={f.phone} onChange={setPhone} className={inputCls} data-testid={`${p}-phone`} />
      {type === "volunteer" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <input placeholder={ui.district} value={f.district} onChange={set("district")} className={inputCls} data-testid={`${p}-district`} />
          <input placeholder={ui.area} value={f.area} onChange={set("area")} className={inputCls} data-testid={`${p}-area`} />
        </div>
      )}
      <textarea required={type === "contact"} rows={type === "contact" ? 5 : 3} placeholder={ui.message} value={f.message} onChange={set("message")} className={inputCls} data-testid={`${p}-message`} />
      <label className="flex items-start gap-3 text-sm text-s-muted cursor-pointer">
        <input type="checkbox" required checked={f.kvkk} onChange={set("kvkk")} className="mt-1 w-4 h-4 accent-[color:var(--s-primary)]" data-testid={type === "volunteer" ? "kvkk-consent-checkbox" : `${p}-kvkk`} />
        <span>{ui.kvkk} <button type="button" className="underline" onClick={() => setShowKvkk(true)} data-testid={`${p}-kvkk-toggle`}>KVKK</button></span>
      </label>
      <Dialog open={showKvkk} onOpenChange={setShowKvkk}>
        <DialogContent data-theme={site.theme?.mode === "adaylik" ? "adaylik" : "normal"} className="max-w-2xl max-h-[85vh] overflow-y-auto bg-s-surface text-s-text border-site-border" data-testid={`${p}-kvkk-text`}>
          <DialogHeader><DialogTitle className="font-heading">KVKK Aydınlatma Metni</DialogTitle></DialogHeader>
          <div className="prose-site text-sm" dangerouslySetInnerHTML={{ __html: lazyImgs(mdToHtml(t(site.general?.kvkk))) }} />
          <div className="flex justify-end gap-3 pt-4 border-t border-site-border">
            <button type="button" onClick={() => setShowKvkk(false)} className="px-5 py-2.5 rounded-full border border-site-border text-sm hover:bg-white/5 transition-colors" data-testid={`${p}-kvkk-close`}>Kapat</button>
            <button type="button" onClick={() => { setF((x) => ({ ...x, kvkk: true })); setShowKvkk(false); }} className="px-5 py-2.5 rounded-full bg-s-primary text-s-onprimary text-sm font-semibold hover:opacity-90 transition-opacity" data-testid={`${p}-kvkk-accept`}>Okudum, onaylıyorum</button>
          </div>
        </DialogContent>
      </Dialog>
      {state.error && <p className="text-sm text-red-500" data-testid={`${p}-error`}>{state.error}</p>}
      <button disabled={state.busy} data-testid={type === "volunteer" ? "volunteer-form-submit-button" : "contact-form-submit-button"}
        className="w-full sm:w-auto rounded-[var(--s-radius)] bg-s-primary text-s-onprimary px-8 py-3.5 font-semibold disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
        {state.busy ? ui.sending : ui.send}
      </button>
      {captchaEnabled && (
        <p className="text-[10px] text-s-muted mt-2" data-testid={`${p}-recaptcha-note`}>
          Bu form Google reCAPTCHA ile korunmaktadır. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Gizlilik</a> ·{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Şartlar</a>
        </p>
      )}
    </form>
  );
}
